import { skipToken } from '@apollo/client/react'
import { Button, FormControlLabel } from '@mui/material'
import { uniq, uniqBy } from 'lodash-es'
import {
  forwardRef,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Virtuoso } from 'react-virtuoso'
import { tv } from 'tailwind-variants'
import IcBaselineDeleteOutline from '~icons/ic/baseline-delete-outline'
import IcOutlineHistory from '~icons/ic/outline-history'
import { openSignModal } from 'src/components/Account/utils'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  GenerationModel,
  ListGenerationModelsWithTrainingTaskQueryVariables,
} from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import {
  useGetTrainingTaskById,
  useInfiniteListGenerationModelsWithTrainingTask,
} from 'src/services/apollo/generation-model'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { getNodesFromConnection } from 'src/utils/graphql'
import { getThumbnailUrl } from 'src/utils/media'
import { useQs } from 'src/utils/qs'
import { track } from 'src/utils/tracker'
import Empty from '../Empty'
import { TaskMediaPicker } from '../GenerationTask/TaskMediaPicker'
import {
  MediaSelectPreviewPanelItemsProgressContext,
  MediaSelectPreviewPanelItemsRefContext,
  useImageItemListCount,
  useSetImageItemList,
} from '../Media/MediaSelectPreviewPanel/context'
import { Preloader } from '../Preloader/Preloader'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
} from '../ui/Modal'
import Text from '../ui/Text'
import {
  DatasetDialogItemsRefContext,
  DatasetDialogItemsRefContextProvider,
  DatasetDialogListContext,
  DatasetDialogListContextProvider,
  ImageItemsSourceContext,
  TrainLoraContext,
  TrainLoraDatasetContext,
} from './context'
import { MAX_ALLOWED_TRAIN_LORA_MEDIA } from './utils'

const DatasetListContainerInDialog = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => <div ref={ref} className="flex flex-col gap-1" {...props} />)

export function useSyncDatasetFromTrainingTaskQs() {
  const [, setDatasetMode] = useContext(TrainLoraDatasetContext)
  const setImageItemId = useSetImageItemList()
  const itemsSourceRef = useContext(ImageItemsSourceContext)
  const [{ trainingTaskId }, setQs] = useQs()
  const { data, loading } = useGetTrainingTaskById(trainingTaskId || skipToken)
  const onceRef = useRef(false)

  const onAddDatasetImages = useCallback((mediaIds: string[]) => {
    setImageItemId(prev => {
      return uniq([...prev, ...mediaIds])
    })
    itemsSourceRef.current &&
      mediaIds.forEach(mediaId => {
        itemsSourceRef.current?.set(mediaId, 'dataset')
      })
  }, [])

  useEffect(() => {
    if (loading) return
    if (data && !onceRef.current) {
      const mediaIds = data.trainingTask?.parameters.mediaIds ?? []
      if (mediaIds.length > 0) {
        setDatasetMode({
          datasetMode: 'reuse',
          datasetId: trainingTaskId,
        })
        onAddDatasetImages(mediaIds)
      }
      onceRef.current = true
    }
    setQs(prev => ({ ...prev, trainingTaskId: null }))
  }, [trainingTaskId, loading, data?.trainingTask?.id])
}

function TrainLoraImportButtonsBar(props: {
  variant?: 'at-top' | 'at-bottom'
  onUploadClick?: () => void
}) {
  const { variant = 'at-top' } = props
  useSyncDatasetFromTrainingTaskQs()
  const { t } = useTranslation(['market'])
  const [openTaskHistory, setOpenTaskHistory] = useState(false)
  const [openDatasetHistory, setOpenDatasetHistory] = useState(false)
  const [{ datasetMode }, setDatasetMode] = useContext(TrainLoraDatasetContext)
  const { me } = useMe()

  const isAtBottom = variant === 'at-bottom'

  return (
    <div
      className={cx(
        'flex items-center gap-2 relative',
        isAtBottom
          ? 'mt-3 justify-center text-sm text-neutral-500 dark:text-neutral-300'
          : 'px-4 pt-4 justify-end',
      )}
    >
      {datasetMode === 'reuse' && (
        <Button
          variant="outlined"
          className="rounded-full text-xs"
          onClick={() => {
            track('switch_to_default_mode_click', {
              location: isAtBottom ? 'bottom' : 'top',
            })
            setDatasetMode(prev => ({
              ...prev,
              datasetMode: prev.datasetMode === 'default' ? 'reuse' : 'default',
              datasetId: undefined,
            }))
          }}
        >
          {t('market:train-lora.image-select.switch-to-default-mode')}
        </Button>
      )}
      {!isAtBottom && props.onUploadClick && (
        <Button
          variant="outlined"
          className="rounded-full text-xs"
          disabled={datasetMode !== 'default'}
          onClick={() => {
            if (!me?.id) {
              openSignModal()
            } else {
              props.onUploadClick?.()
            }
          }}
        >
          {t('market:train-lora.add-image.label')}
        </Button>
      )}
      <Button
        variant="outlined"
        className="rounded-full text-xs"
        disabled={datasetMode !== 'default'}
        onClick={() => {
          track('import_generation_history_click', {
            location: isAtBottom ? 'bottom' : 'top',
          })
          if (!me?.id) {
            openSignModal()
          } else {
            setOpenTaskHistory(true)
          }
        }}
      >
        {t('market:train-lora.image-select.import-from-history')}
      </Button>
      <Button
        variant="outlined"
        className="rounded-full text-xs"
        disabled={datasetMode !== 'default'}
        onClick={() => {
          track('import_dataset_click', {
            location: isAtBottom ? 'bottom' : 'top',
          })
          if (!me?.id) {
            openSignModal()
          } else {
            setOpenDatasetHistory(true)
          }
        }}
      >
        {t(
          'market:train-lora.image-select.dataset.import-from-previous-datasets',
        )}
      </Button>
      {!isAtBottom && <ClearImagesBtn />}
      <TaskMediaPickerForImport
        open={openTaskHistory}
        onClose={() => setOpenTaskHistory(false)}
      />
      <UIDialogResponsive
        open={openDatasetHistory}
        onClose={() => setOpenDatasetHistory(false)}
        dismissable
      >
        <DatasetDialogListContextProvider>
          <DatasetDialogItemsRefContextProvider>
            <DatasetImageSelector
              onClose={() => setOpenDatasetHistory(false)}
            />
          </DatasetDialogItemsRefContextProvider>
        </DatasetDialogListContextProvider>
      </UIDialogResponsive>
    </div>
  )
}

/**
 * Standalone button to import from generation history.
 * Used as a primary entry in the empty state.
 */
const importHistoryButtonStyles = tv({
  base: 'inline-flex items-center gap-2 font-medium transition-colors',
  variants: {
    variant: {
      link: 'text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 text-sm font-bold',
      icon: 'bg-violet-50 dark:bg-violet-950/30 p-3 rounded-full text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/40',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
    },
  },
})

export function TrainLoraImportHistoryButton(props: {
  variant: 'link' | 'icon'
}) {
  const { t } = useTranslation(['market'])
  const [openTaskHistory, setOpenTaskHistory] = useState(false)
  const [{ datasetMode }] = useContext(TrainLoraDatasetContext)
  const { me } = useMe()

  return (
    <>
      <AriaButton
        className={importHistoryButtonStyles({
          variant: props.variant,
          disabled: datasetMode !== 'default',
        })}
        isDisabled={datasetMode !== 'default'}
        onPress={() => {
          track('import_generation_history_click', { location: 'empty-state' })
          if (!me?.id) {
            openSignModal()
          } else {
            setOpenTaskHistory(true)
          }
        }}
      >
        <IcOutlineHistory
          className={props.variant === 'icon' ? 'size-7' : 'size-4'}
        />
        {props.variant !== 'icon' &&
          t('market:train-lora.image-select.import-from-history')}
      </AriaButton>
      <TaskMediaPickerForImport
        open={openTaskHistory}
        onClose={() => setOpenTaskHistory(false)}
      />
    </>
  )
}

/**
 * Standalone button to import from previous datasets.
 * Used as a secondary entry in the empty state.
 */
const importDatasetButtonStyles = tv({
  base: 'inline-flex items-center gap-1.5 font-medium transition-colors text-sm',
  variants: {
    variant: {
      secondary:
        'flex-1 justify-center rounded-lg px-4 py-2.5 bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 shadow-sm',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
    },
  },
})

export function TrainLoraImportDatasetButton(props: { variant: 'secondary' }) {
  const { t } = useTranslation(['market'])
  const [openDatasetHistory, setOpenDatasetHistory] = useState(false)
  const [{ datasetMode }] = useContext(TrainLoraDatasetContext)
  const { me } = useMe()

  return (
    <>
      <AriaButton
        className={importDatasetButtonStyles({
          variant: props.variant,
          disabled: datasetMode !== 'default',
        })}
        isDisabled={datasetMode !== 'default'}
        onPress={() => {
          track('import_dataset_click', { location: 'empty-state' })
          if (!me?.id) {
            openSignModal()
          } else {
            setOpenDatasetHistory(true)
          }
        }}
      >
        {t(
          'market:train-lora.image-select.dataset.import-from-previous-datasets',
        )}
      </AriaButton>
      <UIDialogResponsive
        open={openDatasetHistory}
        onClose={() => setOpenDatasetHistory(false)}
        dismissable
      >
        <DatasetDialogListContextProvider>
          <DatasetDialogItemsRefContextProvider>
            <DatasetImageSelector
              onClose={() => setOpenDatasetHistory(false)}
            />
          </DatasetDialogItemsRefContextProvider>
        </DatasetDialogListContextProvider>
      </UIDialogResponsive>
    </>
  )
}

export function ClearImagesBtn() {
  const [{ datasetMode }] = useContext(TrainLoraDatasetContext)
  const setImageItems = useSetImageItemList()
  const uploadItemsRef = useContext(MediaSelectPreviewPanelItemsRefContext)
  const itemsSourceRef = useContext(ImageItemsSourceContext)
  const [, setUploadProgress] = useContext(
    MediaSelectPreviewPanelItemsProgressContext,
  )

  const onClick = async () => {
    try {
      track('selected_image_clear_click')

      const confirmClear = await UIDialog.confirm({
        heading: (
          <Text i18nKey="market:train-lora.image-select.clear-images.dialog.title" />
        ),
        content: (
          <Text i18nKey="market:train-lora.image-select.clear-images.dialog.content" />
        ),
      })
      if (!confirmClear) return

      track('selected_image_clear_confirm_click')
      // Cleanup contexts
      setImageItems([])
      itemsSourceRef.current?.clear()
      // Abort all uploading images, if any
      uploadItemsRef.current?.forEach(item => {
        item.controller.abort()
      })
      uploadItemsRef.current?.clear()
      setUploadProgress({})
    } catch (e: any) {
      toast.error(e.message)
    }
  }
  return (
    <Button
      onClick={onClick}
      variant="outlined"
      className="min-w-0 rounded-full p-1"
      disabled={datasetMode === 'reuse'}
    >
      <IcBaselineDeleteOutline />
    </Button>
  )
}

function TaskMediaPickerForImport(props: {
  open: boolean
  onClose: () => void
}) {
  const setImageItemList = useSetImageItemList()
  const itemsSourceRef = useContext(ImageItemsSourceContext)

  const onSubmit = useCallback(
    (mediaIds: string[]) => {
      setImageItemList(prev => uniq([...prev, ...mediaIds]))
      itemsSourceRef.current &&
        mediaIds.forEach(mediaId => {
          itemsSourceRef.current?.set(mediaId, 'task')
        })
    },
    [setImageItemList, itemsSourceRef],
  )

  return (
    <TaskMediaPicker
      open={props.open}
      onClose={props.onClose}
      multiple
      onSubmit={onSubmit}
    />
  )
}

function DatasetImageSelector(props: { onClose: () => void }) {
  const { t } = useTranslation()
  const imagesCount = useImageItemListCount()
  const [selectedDatasets] = useContext(DatasetDialogListContext)
  const itemsRef = useContext(DatasetDialogItemsRefContext)

  const selectedTotalImages = useMemo(
    () =>
      selectedDatasets.reduce((acc, cur) => {
        return acc + (itemsRef?.current!.get(cur) ?? []).length
      }, imagesCount),
    [imagesCount, selectedDatasets],
  )

  return (
    <>
      <UIDialogHeader
        heading={
          <div className="">
            <h1 className="font-medium">
              {t('market:train-lora.image-select.dataset.import-from-dataset')}
            </h1>
            <p className="font-medium text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              {t('market:train-lora.image-select.import-total-remaining', {
                total: MAX_ALLOWED_TRAIN_LORA_MEDIA,
                remaining: MAX_ALLOWED_TRAIN_LORA_MEDIA - selectedTotalImages,
              })}
            </p>
          </div>
        }
      />
      <UIDialogContent>
        <div className="[--list-height:calc(var(--visible-height)-4rem-24px-44px-32px-80px)] md:[--list-height:320px] md:w-[320px]">
          <Suspense fallback={<Preloader />}>
            <DatasetList />
          </Suspense>
        </div>
      </UIDialogContent>
      <UIDialogFooter>
        <ImportDatasetBtn className="ml-auto" onClose={props.onClose} />
      </UIDialogFooter>
    </>
  )
}

const PAGE_SIZE = 20
function DatasetList() {
  const { t } = useTranslation(['market'])
  const { data: userInfo } = useMyInfo()

  const parseArgs: Parameters<
    typeof useInfiniteListGenerationModelsWithTrainingTask
  >[0] = useMemo(() => {
    /** Don't start fetching unless user has logged in */
    if (!userInfo?.id) return skipToken

    // PosSeq is false
    const args = {
      last: PAGE_SIZE,
      authorId: userInfo.id,
      type: GenerationModelType.AnyUserLora,
    } satisfies ListGenerationModelsWithTrainingTaskQueryVariables

    return args
  }, [userInfo?.id])

  const {
    data,
    loading: modelsLoading,
    fetchMore,
    loadingMore,
  } = useInfiniteListGenerationModelsWithTrainingTask(parseArgs)

  const models = useMemo(() => {
    if (!data) return []
    const nodes = uniqBy(getNodesFromConnection(data), 'id')
    return nodes.reverse()
  }, [data])

  const hasNext = useMemo(() => {
    return data?.pageInfo?.hasPreviousPage
  }, [data?.pageInfo])

  const loadingMoreModels = loadingMore || modelsLoading

  const loadMore = useCallback(() => {
    if (!loadingMoreModels && hasNext) {
      fetchMore({
        variables: {
          before: data?.pageInfo.startCursor,
          last: PAGE_SIZE,
        } as Partial<ListGenerationModelsWithTrainingTaskQueryVariables>,
      })
    }
  }, [loadingMoreModels, hasNext, fetchMore, data])

  const totalCount = useMemo(() => {
    return data?.totalCount
  }, [data?.totalCount])

  if (models.length < 1) {
    if (modelsLoading) return <Preloader />
    return (
      <Empty
        className="text-xl"
        description={t(
          'market:train-lora.image-select.dataset.empty-placeholder',
        )}
      />
    )
  }

  return (
    <Virtuoso
      className="w-full min-h-[140px]"
      style={{
        height: 'var(--list-height)',
      }}
      totalCount={totalCount}
      data={models}
      endReached={loadMore}
      itemContent={(_, item) => (
        <DatasetHistoryItem model={item} key={item.id} />
      )}
      components={{
        List: DatasetListContainerInDialog,
      }}
    ></Virtuoso>
  )
}

function ImportDatasetBtn(props: StyleProps & { onClose?: () => void }) {
  const { t } = useTranslation(['market'])
  const [, setTrainLoraContext] = useContext(TrainLoraContext)
  const setImageItemId = useSetImageItemList()
  const [selectedDatasets] = useContext(DatasetDialogListContext)
  const itemsSourceRef = useContext(ImageItemsSourceContext)

  const itemsRef = useContext(DatasetDialogItemsRefContext)
  const onAddDatasetImages = useCallback(() => {
    setTrainLoraContext(prev => ({
      ...prev,
      importedLoraList: [...prev.importedLoraList, ...selectedDatasets],
    }))
    const selectedMediaIds = selectedDatasets.reduce((acc: string[], curr) => {
      const mediaIds = itemsRef.current?.get(curr) ?? []
      return [...acc, ...mediaIds]
    }, [])
    setImageItemId(prev => {
      return uniq([...prev, ...selectedMediaIds])
    })
    itemsSourceRef.current &&
      selectedMediaIds.forEach(mediaId => {
        itemsSourceRef.current?.set(mediaId, 'dataset')
      })
    props.onClose?.()
  }, [selectedDatasets])

  return (
    <button
      disabled={selectedDatasets.length < 1}
      className={cx(
        'rounded-full py-3 px-6 font-medium bg-violet-600 text-white text-xs',
        'disabled:cursor-not-allowed disabled:opacity-50',
        props.className,
      )}
      onClick={onAddDatasetImages}
    >
      {t('market:train-lora.image-select.dataset.import-datasets')}
    </button>
  )
}

function DatasetHistoryItem(props: { model: GenerationModel }) {
  const { model } = props
  const modelCover = getThumbnailUrl(model.media)
  const imagesCount = useImageItemListCount()
  const datasetMediaIds = model.trainingTask?.parameters.mediaIds ?? []
  const datasetSize = datasetMediaIds.length

  const [selectedDatasets, setSelectedDatasets] = useContext(
    DatasetDialogListContext,
  )

  const itemsRef = useContext(DatasetDialogItemsRefContext)
  if (!itemsRef.current) {
    itemsRef.current = new Map()
  }

  const checked = useMemo(() => {
    return selectedDatasets.includes(model.id)
  }, [selectedDatasets])

  const shouldDisable = useMemo(() => {
    const selectedTotalImages = selectedDatasets.reduce((acc, cur) => {
      return acc + (itemsRef.current!.get(cur) ?? []).length
    }, imagesCount)
    const remaining = MAX_ALLOWED_TRAIN_LORA_MEDIA - selectedTotalImages
    return datasetSize > remaining
  }, [selectedDatasets, imagesCount, datasetSize])

  const onLabelChange = useCallback(() => {
    setSelectedDatasets(prev => {
      let dataset = [...prev]
      const checked = dataset.includes(model.id)
      if (checked) {
        dataset = dataset.filter(i => i !== model.id)
        itemsRef.current!.delete(model.id)
      } else {
        dataset.push(model.id)
        itemsRef.current!.set(model.id, datasetMediaIds)
      }
      return dataset
    })
  }, [model.id, datasetMediaIds])

  return (
    <FormControlLabel
      className={cx(
        'ml-0 mr-0 px-1 py-[6px] w-full flex items-center gap-2 min-w-0 rounded-[4px]',
        checked && 'bg-sky-200 dark:bg-sky-900',
        !checked && shouldDisable && 'cursor-not-allowed text-neutral-400',
      )}
      disabled={!checked && shouldDisable} // Allow user to uncheck
      disableTypography
      label={
        <>
          <img
            className="object-cover size-6 shrink-0 rounded"
            src={modelCover}
          ></img>
          <span className="font-medium text-sm truncate">
            {props.model.title}
          </span>
          <span
            className={cx(
              'ml-auto text-sm',
              shouldDisable && !checked
                ? 'text-neutral-400 dark:text-neutral-600'
                : 'text-neutral-500 dark:text-neutral-300',
            )}
          >
            {datasetSize}/{MAX_ALLOWED_TRAIN_LORA_MEDIA}
          </span>
        </>
      }
      onChange={onLabelChange}
      control={<input type="checkbox" className="sr-only" />}
    ></FormControlLabel>
  )
}
